/** "Amanhã: 7 atendimentos. Enviar lembretes?" — abre as mensagens uma a uma (11.2). */
import { addDays, formatDayLong, formatHourOf, todayIn } from '@lindeza/core';
import { useProfessional, useTomorrow } from '@lindeza/core/hooks';
import { useState } from 'react';
import { messageFor, openWhatsapp, useMessageCtx } from '../../app/useMessages';
import { IconCheck, IconSkipForward, IconWhatsapp } from '../../components/Icons';
import { Avatar, Empty, Sheet } from '../../components/ui';

export function TomorrowSheet({ onClose }: { onClose: () => void }) {
  const items = useTomorrow();
  const ctx = useMessageCtx();
  const prof = useProfessional();
  const tz = prof?.timezone ?? 'America/Sao_Paulo';
  const [sent, setSent] = useState<Set<string>>(new Set());

  const close = () => {
    try {
      localStorage.setItem(`lindeza:reminders:${todayIn(tz)}`, '1');
    } catch {
      // modo privado
    }
    onClose();
  };

  if (!items || !ctx) return null;
  const next = items.find((i) => !sent.has(i.appointment.id));

  const index = next ? items.indexOf(next) : items.length;
  const tomorrow = addDays(todayIn(tz), 1);
  const skip = (id: string) => setSent((s) => new Set(s).add(id));
  return (
    <Sheet
      title="Lembretes de amanhã"
      sub={`${formatDayLong(tomorrow).replace(/^./, (c) => c.toUpperCase())}, ${items.length} ${items.length === 1 ? 'cliente' : 'clientes'}`}
      onClose={close}
    >
      {items.length === 0 ? (
        <Empty>Nenhum atendimento amanhã.</Empty>
      ) : (
        <div className="stack stack--lg">
          <div className="tm-step">
            <span>{next ? `Lembrete ${index + 1} de ${items.length}` : 'Todos os lembretes foram abertos'}</span>
            <span className="tm-bar" aria-hidden="true">
              {items.map((i) => (
                <i key={i.appointment.id} className={sent.has(i.appointment.id) ? 'on' : i === next ? 'now' : undefined} />
              ))}
            </span>
          </div>
          {next ? (
            <>
              <div className="at-head">
                <Avatar name={next.client?.name ?? '?'} />
                <span className="grow">
                  <b className="at-name">{next.client?.name ?? 'Cliente'}</b>
                  <span className="at-sub">
                    {formatHourOf(next.appointment.start_utc, tz)}, {next.service?.name.toLowerCase()}
                  </span>
                </span>
              </div>
              <p className="bubble">{messageFor(ctx, 'reminder', next.appointment, next.client, next.service)}</p>
              <button
                className="btn btn--primary btn--lg btn--block"
                onClick={() => {
                  openWhatsapp(next.client?.whatsapp, messageFor(ctx, 'reminder', next.appointment, next.client, next.service));
                  skip(next.appointment.id);
                }}
              >
                <IconWhatsapp /> Mandar no WhatsApp
              </button>
              <button className="btn btn--ghost btn--block" onClick={() => skip(next.appointment.id)}>
                <IconSkipForward /> Pular {next.client?.name.split(' ')[0] ?? 'cliente'}
              </button>
            </>
          ) : (
            <div className="banner">
              <IconCheck />
              <p className="grow">Pronto. As mensagens de amanhã foram abertas no WhatsApp.</p>
            </div>
          )}
          {items.length > 1 && (
            <div>
              <p className="at-sec-title">{next ? 'Depois dela' : 'Amanhã'}</p>
              <ul className="list list--plain" aria-label="Clientes de amanhã">
                {items
                  .filter((i) => i !== next)
                  .map((i) => (
                    <li key={i.appointment.id}>
                      <button
                        className="list-item"
                        onClick={() => {
                          openWhatsapp(i.client?.whatsapp, messageFor(ctx, 'reminder', i.appointment, i.client, i.service));
                          skip(i.appointment.id);
                        }}
                        aria-label={`Mandar lembrete para ${i.client?.name ?? 'cliente'}`}
                      >
                        <span className="price" style={{ minWidth: 56 }}>
                          {formatHourOf(i.appointment.start_utc, tz)}
                        </span>
                        <span className="grow">
                          <span className="title">{i.client?.name ?? 'Cliente'}</span>
                          <span className="meta">{i.service?.name}</span>
                        </span>
                        {sent.has(i.appointment.id) ? <IconCheck style={{ color: 'var(--success)' }} /> : <IconWhatsapp style={{ color: 'var(--wa)' }} />}
                      </button>
                    </li>
                  ))}
              </ul>
            </div>
          )}
          <button className="btn btn--ghost btn--block" onClick={close}>
            Fechar
          </button>
        </div>
      )}
    </Sheet>
  );
}
